import clsx from 'clsx';
import { Children, forwardRef, type ReactNode, useMemo } from 'react';

import { ActionButton } from './ActionButton.js';
import { ActionItemGroup } from './ActionItemGroup.js';
import { ActionItems } from './ActionItems.js';
import { AppIcon } from './app-icon/AppIcon.js';
import * as styles from './AppHeader.sty.js';
import { Logo } from './Logo.js';
import { Menus } from './Menus.js';
import { Navigation, NavigationItem } from './Navigation.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Divider } from '../divider/Divider.js';
import { HelpMenu } from '../help-menu/HelpMenu.js';

const COMPONENT_NAME = 'AppHeader';

type Slots = {
  actions?: ReactNode;
  menus?: ReactNode;
  navigation: ReactNode;
};

function getItemSlots(children: ReactNode) {
  const slots: Slots = {
    navigation: <AppHeader.Navigation />,
  };

  for (const child of Children.toArray(children)) {
    if (isSlot(child, ActionItems)) {
      slots.actions = child;
      continue;
    }

    if (isSlot(child, HelpMenu)) {
      slots.menus = (
        <>
          {slots.menus}
          {child}
        </>
      );
      continue;
    }

    if (isSlot(child, Menus)) {
      slots.menus = (
        <>
          {child}
          {slots.menus}
        </>
      );
      continue;
    }

    if (isSlot(child, Navigation)) {
      slots.navigation = child;
      continue;
    }

    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn('The used child is not supported by the AppHeader.');
    }
  }
  return slots;
}

/**
 * @public
 */
export interface AppHeaderProps
  extends StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps,
    WithChildren {}

const AppHeaderRoot = forwardRef<HTMLDivElement, AppHeaderProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const slots = useMemo(() => getItemSlots(children), [children]);

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <header
        {...restProps}
        {...behavioralTrackingProps}
        ref={forwardedRef}
        className={clsx(styles.header, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-dt-component={COMPONENT_NAME}
      >
        <OverlayStackingProvider>
          {slots.navigation}
          {slots.actions}
          {slots.actions && slots.menus ? (
            <Divider
              orientation="vertical"
              flexItem={false}
              style={{ height: 20 }}
              aria-hidden
            />
          ) : null}
          {slots.menus}
        </OverlayStackingProvider>
      </header>
    );
  },
);

(AppHeaderRoot as typeof AppHeaderRoot & { displayName: string }).displayName =
  'AppHeader';

/**
 * Use the `AppHeader` to provide a persistent responsive header with app navigation, home button, and global action items available throughout your app.
 * @public
 */
export const AppHeader = Object.assign(AppHeaderRoot, {
  // DEPRECATION: [APPDEV-14264] BREAKING-CHANGE: AppHeader: Remove old NavItems API.
  /**
   * @deprecated use {@link AppHeader.Logo}
   */
  AppNavLink: Logo,
  // DEPRECATION: [APPDEV-14264] BREAKING-CHANGE: AppHeader: Remove old NavItems API.
  /**
   * @deprecated use {@link AppHeader.Navigation}
   */
  NavItems: Navigation,
  // DEPRECATION: [APPDEV-14264] BREAKING-CHANGE: AppHeader: Remove old NavItems API.
  /**
   * @deprecated use {@link AppHeader.NavigationItem}
   */
  NavItem: NavigationItem,
  AppIcon,
  ActionItems,
  ActionItemGroup,
  ActionButton,
  Logo,
  Menus,
  Navigation,
  NavigationItem,
});
